import {useEffect, useState} from 'react'
import dayjs from 'dayjs'
import { Button, Space } from 'antd'
import {Modal, Text, Empty, Loader, ProfileCircleIcon, WarningIcon} from 'common/components'
import {formatDate} from 'common/helpers'
import {useOpen} from 'common/hooks'
import {useGetReconciliations, useReconciliationMatch} from '..'
import { PAGE_SIZE } from '../constants'
import { useStore, setReconciliationPageNumber as setPage } from '../store'
import { StatementDataType, TransactionDataType } from '..'
import styles from './Outlet.module.scss'
import { Pagination } from './Pagination'
import { Pair } from './Pair'
import { Title } from './Title'
import { Row } from './Row'
import { Slip } from './Slip'
import { EmptySlip } from './Slip.Empty'
// import { useMockStore, setMockMatch } from './store'

// const mockStatement = {
//   id: '1234',
//   date:'2023-3-26',
//   client:'client name', 
//   item: 'drawdown',
//   description:'first drawdown',
//   spent:33434
// }
// const mockTransaction = {
//   id: '5678',
//   date:'2023-3-26',
//   account:'account name',
//   reference:'reference',
//   notes:'notes',
//   spent:34344,
// }
export default function OutletReconciliation() {
  const page = useStore((state) => state.pageNumberReconciliation)
  const {data} = useGetReconciliations({ page })
  // const data = useMockStore((state) => state)
  // console.log(data?.data)

  return (
    <>
      {!data ? (
        <Loader />
      ) : !data.data?.length ? (
        <Empty
          icon={<ProfileCircleIcon width={40} height={40} />}
          title="data"
        />
      ) : (
        <>
          <Pair
            left={ <Title>Review your Xero statement lines...</Title> }
            right={ <Title>...then match with your transactions in VC Online</Title> }
          />
          { data.data.map((x:any, index:number) => (
            <ReconciliationRow key={index}
              statement={{
                id:x.uuid,
                date: formatDate(x.date),
                spent: (x.amount > 0) ? x.amount : undefined,
                received: (x.amount < 0) ? (-1 * x.amount) : undefined,
                description: x.description,
                client: x.project?.title || '', 
                item: x.reference || '',
              }}
              transaction={!!x.transaction && {
                id:x.transaction.uuid,
                date: formatDate(x.transaction.date),
                reference:x.transaction.type,
                notes:x.transaction.notes,
                spent: (x.transaction.amount > 0) ? x.transaction.amount : undefined,
                received: (x.transaction.amount < 0) ? (-1 * x.transaction.amount) : undefined,
                account: x.project?.title || '',
              }}
            />
          )) }
          {/* <ReconciliationRow
            statement={mockStatement}
          /> */}
          {/* <ReconciliationRow
            statement={mockStatement}
            transaction={mockTransaction}
          />
          <ReconciliationRow
            transaction={mockTransaction}
          />
          <ReconciliationRow
            transaction={mockTransaction}
          /> */}
        </>
      )}

      { !!data?.meta?.total &&
        <Pagination
          showSizeChanger={false}
          current={page}
          total={data?.meta?.total ?? 0}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      }

    </>
  )
}

const ReconciliationRow = ({ statement, transaction }:{
  statement?: StatementDataType;
  transaction?: TransactionDataType;
}) => {
  const {mutate: setMatch, isMutating} = useReconciliationMatch()
  const [date, setDate] = useState<any>(null)
  const [what, setWhat] = useState<string>('')
  const [why, setWhy] = useState<string>('')

  const onDateChanged = (d:any) => {
    if (!!d) {
      setDate(dayjs(d).format('YYYY-MM-DD'))
    } else {
      setDate(null)
    }
  }

  const isTransValid = !!transaction?.id
  const isBtnDisabled = !!isMutating || ( (!isTransValid) && ((!what) || (!why) || (!date)) )
  const isDateMatch = ((!!date) && (dayjs(date).isSame(statement.date, 'day'))) ||
                      ((!date) && (!!statement?.date) && (dayjs(statement.date).isSame(transaction?.date, 'day')))

  useEffect(() => {
    transaction?.date && onDateChanged(transaction.date)
  }, [transaction?.date])

  const onMatchClick = () => {
    const data: any = isTransValid ? {
      statementId: statement?.id,
      transactionUuid: transaction?.id,
    } : {
      statementId: statement?.id,
      type: what,
      notes: why,
    }
    if (!!date) {
      data.date = date
      setMatch(data)
      transaction?.date ? onDateChanged(transaction.date) : setDate(null)
    }
  }

  return (
    <Row>
      <Pair
        left={ !!statement && <Slip {...statement} /> }
        right={ !!isTransValid ? 
          <Slip {...transaction} match={!!statement} onDateChange={onDateChanged} /> : 
          <EmptySlip targetId={statement?.id} onDateChange={onDateChanged} onWhatChange={setWhat} onWhyChange={setWhy} />}
        middle={
          (isDateMatch) ? <Button disabled={isBtnDisabled} onClick={onMatchClick}>OK</Button> : <ConfirmButton disabled={isBtnDisabled} onConfirm={onMatchClick} />
        }
      />
    </Row>
  )
}

const ConfirmButton = ({disabled, onConfirm}:{
  disabled?: boolean;
  onConfirm: () => void;
}) => {
  const [isOpen, handleOpen, handleClose] = useOpen()
  return (
    <>
    <Button disabled={disabled} onClick={handleOpen}>OK</Button>
    
    <Modal
      width={760}
      open={isOpen}
      onCancel={handleClose}
    >
      <div className={styles['warning-modal']}>
        <WarningIcon />
        <Text mt={12} size="2xl" weight="medium" className={styles['warning-text']}>
          Transaction Mismatch Detected
        </Text>
        <Text mt={32} mb={64} size="xl" weight="medium" style={{textAlign: 'center'}}>
  The system has identified a discrepancy in the recorded transaction dates.
  Do you want to proceed with the reconciliation?
        </Text>
        <Space size="large">
          <Button className={styles['warning-button']} onClick={handleClose}>
            Cancel
          </Button>
          <Button className={styles['warning-button']} onClick={() => {handleClose(); onConfirm();}}>
            Continue
          </Button>
        </Space>
      </div>
    </Modal>
    </>
  )
}
